import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  BreadcrumbHeading,
  Dropdown,
  IOption,
  PrimaryButton,
  PrimaryButtonProps,
  Refresh,
  SecondaryButton,
  useClickOutside,
} from '@weareopenr/component-library';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { getSyncEndpoints, ISyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import useBreadcrumbs from 'src/hooks/useBreadcrumbs/useBreadcrumbs';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus';
import styles from './PageHeadingWithSync.module.scss';

export type IHeadingOptionMenuItem = IOption;

export interface IPageHeadingWithSyncProps {
  optionMenuItems: IHeadingOptionMenuItem[];
  entityLabel: string;
  syncEndpointName?: string;
  isSyncEnabled: boolean;
  isSyncInProgress: boolean;
  triggerSync: (instanceId?: string) => void;
  /**
   * Whether this page's sync trigger actually acts on the chosen instance. Off by default, so a
   * page whose trigger ignores the argument shows one plain button rather than a picker that
   * silently syncs whatever the backend picks.
   */
  supportsInstanceSelection?: boolean;
  rejectSync: () => void;
  syncStatus?: EProductLibraryStatus;
  /**
   * A second source of truth for "a sync is waiting to be reviewed". `syncStatus` comes from an
   * endpoint that reports one connected instance at a time, so a review pending on one instance can
   * be invisible there. Pages that have event-scoped evidence of their own can assert it here.
   */
  hasPendingReview?: boolean;
  reviewRoutePath: string;
  renderReviewModal: (args: { isOpen: boolean; closeModal: () => void }) => ReactNode;
  primaryAction?: ReactElement<PrimaryButtonProps>;
  extraModals?: ReactNode;
}

// With several instances connected, the heading reports the freshest of them rather than whichever
// happens to come back first.
const getLastSyncTime = (endpoints: ISyncEndpoint[] = []): Date | null => {
  const syncTimes = endpoints
    .map((endpoint) => endpoint.lastUpdated)
    .filter((lastUpdated): lastUpdated is string => !!lastUpdated);
  if (syncTimes.length === 0) return null;
  const [mostRecent] = [...syncTimes].sort((a, b) => b.localeCompare(a));
  return new Date(mostRecent);
};

export const PageHeadingWithSync = ({
  optionMenuItems,
  entityLabel,
  syncEndpointName,
  isSyncEnabled,
  isSyncInProgress,
  triggerSync,
  supportsInstanceSelection = false,
  rejectSync,
  syncStatus,
  hasPendingReview = false,
  reviewRoutePath,
  renderReviewModal,
  primaryAction,
  extraModals,
}: IPageHeadingWithSyncProps): ReactElement => {
  const { data: externalSystems } = useExternalSystems();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = useBreadcrumbs();

  const searchParams = new URLSearchParams(location.search);
  const reviewModalRequested = searchParams.get('review') === 'true';

  const syncEndpoints = useMemo(
    () => getSyncEndpoints(externalSystems, syncEndpointName),
    [externalSystems, syncEndpointName],
  );
  const hasInstanceChoice = supportsInstanceSelection && syncEndpoints.length > 1;
  // Named only when there's no ambiguity. With several endpoints and no picker, sending the first
  // one would pick an instance on the user's behalf; the backend decides instead.
  const [unambiguousEndpoint] = syncEndpoints.length === 1 ? syncEndpoints : [];
  const isSyncFeatureEnabled = useMemo(() => syncEndpoints.length > 0 && isSyncEnabled, [syncEndpoints, isSyncEnabled]);
  const lastSyncTime = getLastSyncTime(syncEndpoints);

  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);

  const isPendingReview = hasPendingReview || syncStatus === EProductLibraryStatus.PENDING_REVIEW;

  useEffect(() => {
    if (reviewModalRequested && isPendingReview) {
      setIsReviewModalOpen(true);
    }
  }, [reviewModalRequested, isPendingReview]);

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    if (reviewModalRequested) {
      navigate(reviewRoutePath);
    }
  };

  return (
    <>
      {renderReviewModal({ isOpen: isReviewModalOpen, closeModal: closeReviewModal })}
      {extraModals}
      <div className={styles['info-container']}>
        <BreadcrumbHeading
          breadcrumbs={breadcrumbs}
          optionMenuItems={optionMenuItems}
          primaryButton={
            isPendingReview ? (
              <PrimaryButton onClick={() => setIsReviewModalOpen(true)} icon={Refresh}>
                Review Sync
              </PrimaryButton>
            ) : (
              primaryAction
            )
          }
          secondaryButton={
            isSyncFeatureEnabled ? (
              isPendingReview ? (
                <SecondaryButton onClick={rejectSync} colour="tomato">
                  Reject Sync
                </SecondaryButton>
              ) : hasInstanceChoice ? (
                <SyncEntryMenu
                  entityLabel={entityLabel}
                  isSyncInProgress={isSyncInProgress}
                  syncEndpoints={syncEndpoints}
                  onSync={triggerSync}
                />
              ) : (
                <SecondaryButton
                  onClick={() => triggerSync(unambiguousEndpoint?.instanceId)}
                  icon={Refresh}
                  disabled={isSyncInProgress}
                >
                  {isSyncInProgress ? 'Sync in Progress...' : `Sync ${entityLabel}`}
                </SecondaryButton>
              )
            ) : undefined
          }
          subtitle={
            lastSyncTime
              ? `Last synchronised at ${formatDate(lastSyncTime)}, ${formatTime(lastSyncTime)}`
              : 'Not yet synchronised'
          }
        />
      </div>
    </>
  );
};

interface ISyncEntryMenuProps {
  entityLabel: string;
  isSyncInProgress: boolean;
  syncEndpoints: ISyncEndpoint[];
  onSync: (instanceId: string) => void;
}

const SyncEntryMenu = ({ entityLabel, isSyncInProgress, syncEndpoints, onSync }: ISyncEntryMenuProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  const options: IOption[] = syncEndpoints.map((endpoint) => ({
    id: endpoint.instanceId,
    name: endpoint.instanceName,
    onOptionClick: () => {
      onSync(endpoint.instanceId);
      setIsOpen(false);
    },
  }));

  return (
    <div ref={ref} className={styles['sync-menu']}>
      <SecondaryButton icon={Refresh} disabled={isSyncInProgress} onClick={() => setIsOpen((open) => !open)}>
        {isSyncInProgress ? 'Sync in Progress...' : `Sync ${entityLabel}`}
      </SecondaryButton>
      <Dropdown options={options} isOpen={isOpen} position="bottom" />
    </div>
  );
};
